<template>
  <div>
    <!-- Loading Spinner -->
    <div v-if="showLoader" class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div class="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-yellow-500"></div>
    </div>

    <!-- Table -->
    <div v-else class="relative overflow-x-auto shadow-md sm:rounded-sm mt-5">
      <table class="w-full text-left rtl:text-right text-gray-700">
        <thead>
          <tr class="bg-gray-100">
            <th scope="col" class="py-3 px-6"><div class="flex items-center">#</div></th>
            <th scope="col" class="py-3 px-6"><div class="flex items-center">Visit ID</div></th>
            <th scope="col" class="py-3 px-6"><div class="flex items-center">Patient Name</div></th>
            <th scope="col" class="py-3 px-6"><div class="flex items-center">Patient Unique Code</div></th>
            <th scope="col" class="py-3 px-6"><div class="flex items-center">Age</div></th>
            <th scope="col" class="py-3 px-6"><div class="flex items-center">Gender</div></th>
            <th scope="col" class="py-3 px-6"><div class="flex items-center">Queue Time</div></th>
            <th scope="col" class="py-3 px-6">Action</th>
            <th scope="col" class="py-3 px-6">Close Visit</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(visit, index) in visits" :key="index" class="border-b">
            <td>{{ index + 1 }}</td>
            <td class="px-6 py-2">{{ visit.VisitCode }}</td>
            <td class="px-6 py-2">{{ visit.patient.FullName }}</td>
            <td class="px-6 py-2">{{ visit.patient.unique_code }}</td>
            <td class="px-6 py-2">
              {{ visit.patient.Age }} {{ visit.patient.AgeType == 1 ? "Years" : "Months" }}
            </td>
            <td class="px-6 py-2">{{ visit.patient.Gender }}</td>
            <td class="px-6 py-2">{{ visit.VisitStartTime }}</td>
            <td class="px-6 py-2">
              <button
                @click="handleProcedureModal(visit.VisitId)"
                class="px-4 py-2 text-sm text-white bg-custom-yellow rounded hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="checkingBalance && selectedVisitId === visit.VisitId"
              >
                <span v-if="checkingBalance && selectedVisitId === visit.VisitId">
                  <div class="animate-spin inline-block rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2"></div>
                  Loading...
                </span>
                <span v-else>Continue Procedure</span>
              </button>
            </td>
            <td class="px-6 py-2">
              <CloseVisitButton :visit="visit" @closed="fetchProcedures" />
            </td>
          </tr>
          <tr v-if="visits.length === 0">
            <td colspan="9" class="text-center py-4 text-gray-500">No ongoing procedures</td>
          </tr>
        </tbody>
      </table>

      <PaginationComponent
        v-if="visits.length > 0"
        :totalPages="totalPages"
        :currentPage="currentPage"
        :itemsPerPage="itemsPerPage"
        :totalItems="totalItems"
        @page-changed="goToPage"
        @page-size-changed="changePageSize"
      />
    </div>

    <!-- Procedure Modal -->
    <ProcedurePatientQueueModal
      v-if="showProcedureModal"
      :key="modalKey"
      :show="showProcedureModal"
      :visitId="selectedVisitId"
      :state="modalState"
      @close="closeProcedureModal"
      @submit="handleProcedureSubmit"
    />

    <!-- Insurance Eligible Modal -->
    <div
      v-if="showInsuranceEligible"
      class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
      role="dialog"
      aria-modal="true"
    >
      <div class="bg-white rounded-lg shadow-md p-6 w-full max-w-md mx-4">
        <div class="flex items-center mb-4">
          <svg class="h-6 w-6 text-green-500 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h2 class="text-xl font-bold text-gray-700">Insurance Coverage Available</h2>
        </div>
        <p class="text-gray-600 mb-6">
          This patient has sufficient insurance coverage for this procedure. You can proceed.
        </p>
        <div class="flex justify-end gap-4">
          <button @click="proceedWithProcedure" class="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700">
            Proceed with Procedure
          </button>
          <button @click="showInsuranceEligible = false" class="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400">
            Cancel
          </button>
        </div>
      </div>
    </div>

    <!-- Cash Payment Required Modal -->
    <div
      v-if="showCashPaymentWarning"
      class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
      role="dialog"
      aria-modal="true"
    >
      <div class="bg-white rounded-lg shadow-md p-6 w-full max-w-md mx-4">
        <div class="flex items-center mb-4">
          <svg class="h-6 w-6 text-custom-yellow mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M12 4a8 8 0 100 16 8 8 0 000-16z" />
          </svg>
          <h2 class="text-xl font-bold text-gray-700">Payment Required</h2>
        </div>
        <p class="text-gray-600 mb-6">
          The patient needs to pay
          <span class="font-semibold">KES {{ outstandingBalance }}</span>
          before proceeding with the procedure.
        </p>
        <div class="flex justify-end gap-4">
          <button @click="showCashPaymentWarning = false" class="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400">
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from "@/services/api";
import { formatDateTime } from "@/services/customUtilities";
import ProcedurePatientQueueModal from "../modals/ProcedurePatientQueueModal.vue";
import PaginationComponent from "@/components/shared/pagination.vue";
import CloseVisitButton from "@/components/shared/CloseVisitButton.vue";

export default {
  name: "ConsultationProceduresResultsQueueTab",
  components: {
    ProcedurePatientQueueModal,
    PaginationComponent,
    CloseVisitButton,
  },
  mounted() {
    this.fetchProcedures();
  },
  props: {
    filters: { type: Object },
  },
  data() {
    return {
      showLoader: false,
      modalKey: 0,
      showProcedureModal: false,
      showCashPaymentWarning: false,
      showInsuranceEligible: false,
      checkingBalance: false,
      outstandingBalance: null,
      selectedVisitId: "",
      modalState: "edit",
      visits: [],
      currentPage: 1,
      itemsPerPage: 25,
      totalItems: 0,
      totalPages: 0,
    };
  },
  methods: {
    async handleProcedureModal(visitId) {
      const visit = this.visits.find(v => v.VisitId === visitId);
      if (!visit) return this.$toast.error("Visit not found");

      this.selectedVisitId = visitId;
      this.checkingBalance = true;
      this.showCashPaymentWarning = false;
      this.showInsuranceEligible = false;

      try {
        const coverageRes = await apiClient.verifyProcedureCoverage({
          patient_id: visit.patient.PatientId,
          visit_id: visitId,
        });

        if (!coverageRes.success) throw new Error("Coverage check failed");

        const coverage = coverageRes.data;

        const hasInsurance = coverage.applied_payment_methods?.some(m => m.type === "insurance");
        if (hasInsurance && coverage.is_covered) {
          this.showInsuranceEligible = true;
          return;
        }

        if (coverage.is_covered || coverage.settle_at_end_allowed) {
          this.modalState = "edit";
          this.modalKey++;
          this.showProcedureModal = true;
          this.$emit("closeDropdown");
          return;
        }

        this.outstandingBalance = parseFloat(coverage.remaining_balance || 0).toFixed(2);
        this.showCashPaymentWarning = true;

      } catch (error) {
        console.error("Coverage check error:", error);
        // Fallback: allow through if coverage check fails
        this.modalState = "edit";
        this.modalKey++;
        this.showProcedureModal = true;
        this.$emit("closeDropdown");
      } finally {
        this.checkingBalance = false;
      }
    },

    proceedWithProcedure() {
      this.showInsuranceEligible = false;
      this.modalState = "edit";
      this.modalKey++;
      this.showProcedureModal = true;
      this.$emit("closeDropdown");
    },

    closeProcedureModal() {
      this.selectedVisitId = "";
      this.showProcedureModal = false;
      this.showCashPaymentWarning = false;
      this.showInsuranceEligible = false;
      this.outstandingBalance = null;
      this.checkingBalance = false;
      this.fetchProcedures();
    },

    goToPage(pageNumber) {
      this.currentPage = pageNumber;
      this.fetchProcedures();
    },
    changePageSize(pageSize) {
      this.itemsPerPage = pageSize;
      this.fetchProcedures();
    },
    async fetchProcedures() {
      try {
        this.showLoader = true;
        const response = await apiClient.getProceduresResultsQueue({
          page: this.currentPage,
          per_page: this.itemsPerPage,
        });
        this.showLoader = false;
        if (response.success) {
          this.visits = response.data.map((item) => ({
            ...item,
            VisitStartTime: formatDateTime(item.VisitStartTime),
          }));
          this.totalItems = response.meta.total_items;
          this.totalPages = response.meta.total_pages;
          this.itemsPerPage = response.meta.per_page;
        } else {
          this.$toast.error("Failed to fetch procedure visits");
        }
      } catch (error) {
        this.showLoader = false;
        this.$toast.error("Failed to fetch procedure visits");
        console.log(error);
      }
    },
    async searchProcedures() {
      try {
        this.$emit("search", true);
        const response = await apiClient.searchConsultation({
          ...this.filters,
          visit_type: 'Procedure/Services',
          consultation_status: 'ongoing',
        });
        if (response.success) {
          this.$emit("search", false);
          this.visits = response.data.map((item) => ({
            ...item,
            VisitStartTime: formatDateTime(item.VisitStartTime),
          }));
        } else {
          this.$toast.error("Failed to search procedures");
        }
      } catch (error) {
        this.$emit("search", false);
        this.$toast.error("Failed to search procedures");
        console.log(error);
      }
    },
    handleProcedureSubmit() {
      this.closeProcedureModal();
    },
  },
};
</script>

<style lang="css" scoped></style>